import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRIES, getCountryByCode } from '../../config/countriesConfig';

const CountrySelector = ({ 
  value, 
  onChange, 
  label = 'Nationality',
  required = false,
  error,
  placeholder = 'Select country',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const selectedCountry = value ? getCountryByCode(value) : null;

  // Filter countries by search
  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(search.toLowerCase()) ||
    country.code.toLowerCase().includes(search.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (country) => {
    onChange(country.code);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="space-y-1.5" ref={dropdownRef}>
      {label && (
        <label className="text-xs font-medium text-gray-600">
          {label}
          {required && <span className="text-[#FF8C00] ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full h-11 px-3 bg-white border rounded-lg text-left flex items-center justify-between transition-colors ${
            error 
              ? 'border-[#FF8C00] focus:ring-[#FF8C00]' 
              : 'border-gray-200 hover:border-gray-300 focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20'
          }`}
          data-testid="country-selector"
        >
          {selectedCountry ? (
            <span className="flex items-center gap-2">
              <span className="text-lg">{selectedCountry.flag}</span>
              <span className="text-sm text-black">{selectedCountry.name}</span>
            </span>
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search country..."
                  className="w-full h-9 pl-9 pr-3 text-sm text-black placeholder:text-gray-400 border border-gray-200 rounded-md focus:outline-none focus:border-[#FF8C00]"
                  data-testid="country-search"
                />
              </div>
            </div>

            {/* Country List */}
            <div className="overflow-y-auto max-h-48">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleSelect(country)}
                    className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-50 transition-colors ${
                      value === country.code ? 'bg-[#FF8C00]/5' : ''
                    }`}
                    data-testid={`country-option-${country.code.toLowerCase()}`}
                  >
                    <span className="text-lg">{country.flag}</span>
                    <span className="text-sm text-black">{country.name}</span>
                    {value === country.code && (
                      <span className="ml-auto text-[#FF8C00]">✓</span>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-sm text-gray-400">
                  No countries found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-[#FF8C00]">{error}</p>
      )}
    </div>
  );
};

export default CountrySelector;
